// zod v4 (EN-1568): this is the app's only zod import. It sticks to the subset
// whose behavior is unchanged from v3 (object/array/string/number/boolean,
// .nullable(), .optional(), .parse). No string formats (email/uuid/url),
// record, strict/passthrough or custom error params, so no codemod is needed.
// The parse/null/malformed cases are covered by __tests__/groupUsageApi.test.ts.
import { z } from "zod";
import { apiFetch, expectJsonBody } from "@common/lib/api";
import { usageRangeSearchParams } from "@features/usage/usage.ranges";
import type { UsageRangeParams } from "@features/usage/usage.types";

// The only roles a corporate group membership can carry (mirrors the backend
// GroupRole union). Kept narrow so role-gated UI can't be fed a bogus value.
export type GroupRole = "group_admin" | "group_viewer";

// Runtime schema for the raw wire response. `series` rows are snake_case; the
// `clinics`/`summary` objects are already camelCase on the wire. We parse before
// mapping so a server contract drift surfaces as a clean error (React Query
// isError → ErrorBanner) instead of silent NaN/undefined cells in the table/chart.
const RawGroupUsageResponseSchema = z.object({
    series: z.array(
        z.object({
            bucket: z.string(),
            account_id: z.string(),
            clinic_name: z.string(),
            happy_visits: z.number(),
            total_visits: z.number(),
        }),
    ),
    clinics: z.array(
        z.object({
            accountId: z.string(),
            clinicName: z.string(),
            happyVisits: z.number(),
            totalVisits: z.number(),
            pctOfTotal: z.number(),
            // Per-clinic details are always emitted by the server (defaulted), but kept
            // optional/nullable here so older fixtures and the details-unavailable path parse.
            clinicLocation: z.string().nullable().optional(),
            currentPims: z.string().nullable().optional(),
            happyVisitsAllTime: z.number().optional(),
            lastHappyVisitDate: z.string().nullable().optional(),
            activeDoctors7d: z.number().optional(),
        }),
    ),
    summary: z.object({
        totalHappyVisits: z.number(),
        activeClinics: z.number(),
        priorTotalHappyVisits: z.number().nullable().optional(),
        dataAsOf: z.string().nullable().optional(),
        // Freshest N days excluded from the window (still-maturing docs). Echoed so the UI's
        // "excludes the last N days" note stays in sync with the server constant.
        reportingLagDays: z.number().optional(),
    }),
    detailsAvailable: z.boolean().optional(),
});
type RawGroupUsageResponse = z.infer<typeof RawGroupUsageResponseSchema>;

// Raw wire response for the by-doctor breakdown. Doctor rows are camelCase (mirrors the
// clinic endpoint's `clinics` convention — passed through untouched, NOT the `series`
// snake_case convention); `summary` is already camelCase on the wire.
const RawGroupUsageByDoctorResponseSchema = z.object({
    doctors: z.array(
        z.object({
            accountId: z.string(),
            clinicName: z.string(),
            doctorId: z.string(),
            doctorName: z.string(),
            // Null for the Unassigned sentinel row and for most PIMS-synced doctors.
            email: z.string().nullable(),
            happyVisits: z.number(),
            totalVisits: z.number(),
        }),
    ),
    summary: z.object({
        totalHappyVisits: z.number(),
        activeDoctors: z.number(),
        dataAsOf: z.string().nullable().optional(),
        reportingLagDays: z.number().optional(),
    }),
});
type RawGroupUsageByDoctorResponse = z.infer<typeof RawGroupUsageByDoctorResponseSchema>;

export interface GroupUsageSeriesRow {
    bucket: string;
    accountId: string;
    clinicName: string;
    happyVisits: number;
    totalVisits: number;
}
export interface GroupUsageClinic {
    accountId: string;
    clinicName: string;
    happyVisits: number;
    totalVisits: number;
    pctOfTotal: number;
    // Window-independent per-clinic details (from get_group_clinic_details). Optional so callers/
    // fixtures needn't supply them; the API always includes them (defaulted server-side). When the
    // details RPC fails the API sets `detailsAvailable=false` and these carry fallback values.
    clinicLocation?: string | null;
    currentPims?: string | null;
    happyVisitsAllTime?: number;
    lastHappyVisitDate?: string | null;
    activeDoctors7d?: number;
}
export interface GroupUsageSummary {
    totalHappyVisits: number;
    activeClinics: number;
    // number = a real prior total (0 included); null = the prior window couldn't be
    // computed; undefined = not comparable under a client-side clinic subset filter
    // (see filterGroupUsage). All three suppress the trend delta in the KPI.
    priorTotalHappyVisits?: number | null;
    // ISO timestamp of the rollup's last refresh (MAX updated_at across the group's accounts),
    // or null when never rolled up / the freshness read failed. Drives the "Updated N ago" label.
    dataAsOf?: string | null;
    // Number of freshest calendar days excluded from the window (today + prior day(s)) because
    // their docs are still being generated. Drives the "excludes the last N days" note.
    reportingLagDays?: number;
}
export interface GroupUsageResponse {
    series: GroupUsageSeriesRow[];
    clinics: GroupUsageClinic[];
    summary: GroupUsageSummary;
    // false when the per-clinic details RPC failed server-side; the UI blanks the
    // detail columns rather than rendering the fallback zeros as real data.
    detailsAvailable?: boolean;
}
export interface GroupUsageDoctor {
    accountId: string;
    clinicName: string;
    doctorId: string;
    doctorName: string;
    email: string | null;
    happyVisits: number;
    totalVisits: number;
}
export interface GroupUsageByDoctorResponse {
    doctors: GroupUsageDoctor[];
    summary: {
        totalHappyVisits: number;
        activeDoctors: number;
        dataAsOf?: string | null;
        reportingLagDays?: number;
    };
}
// /group/usage/me is a "who am I" probe fired for every logged-in user. A non-member gets
// 200 with all-null fields (NOT a 401) so it isn't logged as an error in the browser console;
// a member gets a populated accountGroupId + role. Hence every field is nullable.
export interface GroupMe {
    accountGroupId: string | null;
    groupName: string | null;
    role: GroupRole | null;
}

/**
 * Single source of truth for "is this session a corporate group member right now",
 * derived from the LATEST lookup rather than stale cache.
 *
 * React Query keeps the last successful `groupMe` after a background refetch fails,
 * so a mid-session revocation surfaces as a 401/403 layered on top of a stale `data`.
 * Both CorporatePage (self-guard) and PermissionWrapper (entry gate) MUST gate on this
 * one function: `/corporate` lives outside PermissionWrapper, so if their derivations
 * drift a revoked user bounces between the two guards in a redirect loop. Keeping the
 * logic here — not copy-pasted at each call site — makes "the same signal" true by
 * construction. Accepts the raw `useGroupSession()` result (a superset of these fields).
 */
export function deriveGroupMembership(session: { data: GroupMe | undefined; isError: boolean; error: unknown }): {
    isGroupMember: boolean;
    groupRevoked: boolean;
} {
    const status = (session.error as { response?: { status?: number } })?.response?.status;
    const groupRevoked = session.isError && (status === 401 || status === 403);
    // Membership is keyed on a resolved accountGroupId, not merely on `data` being present:
    // /me now returns 200 with a null accountGroupId for non-members, so `!!session.data` alone
    // would misread every regular user as a group member.
    const isGroupMember = !!session.data?.accountGroupId && !groupRevoked;
    return { isGroupMember, groupRevoked };
}

export async function fetchGroupUsageByClinic(params: UsageRangeParams): Promise<GroupUsageResponse> {
    // Throws on a custom range with a missing/invalid endpoint so an unusable selection surfaces
    // as a query error, not a params-less request that falls back to the default window. (EN-854 H1)
    const searchParams = usageRangeSearchParams(params);
    const body = expectJsonBody(
        await apiFetch<RawGroupUsageResponse>("group/usage/by-clinic", { method: "GET", searchParams }),
        "GET group/usage/by-clinic",
    );
    const raw = RawGroupUsageResponseSchema.parse(body);
    return {
        series: raw.series.map((row) => ({
            bucket: row.bucket,
            accountId: row.account_id,
            clinicName: row.clinic_name,
            happyVisits: row.happy_visits,
            totalVisits: row.total_visits,
        })),
        clinics: raw.clinics,
        summary: raw.summary,
        detailsAvailable: raw.detailsAvailable,
    };
}

export async function fetchGroupUsageByDoctor(params: UsageRangeParams): Promise<GroupUsageByDoctorResponse> {
    // Same custom-range validation as fetchGroupUsageByClinic — throws rather than silently
    // falling back to the default window. (EN-854 H1)
    const searchParams = usageRangeSearchParams(params);
    const body = expectJsonBody(
        await apiFetch<RawGroupUsageByDoctorResponse>("group/usage/by-doctor", { method: "GET", searchParams }),
        "GET group/usage/by-doctor",
    );
    const raw = RawGroupUsageByDoctorResponseSchema.parse(body);
    return {
        doctors: raw.doctors,
        summary: raw.summary,
    };
}

export async function fetchGroupMe(): Promise<GroupMe> {
    return expectJsonBody(
        // A 401 here is the common case (user isn't a corporate-group member), not a
        // bug — skip Sentry so every non-group login doesn't emit a noise error event.
        await apiFetch<GroupMe>("group/usage/me", { method: "GET", skipSentryOnStatuses: [401] }),
        "GET group/usage/me",
    );
}
